/*
 * Подмножество событий AG-UI 1.0 для журнала разговора 006 (specs/006-agent-client/contracts/thread-events.md §2,
 * research R11). Схемы переписаны на Zod по `spec/1.0/schema.json` из ag-ui-protocol/ag-ui@89cedd3.
 *
 * Copyright (c) 2025 AG-UI contributors. MIT License — packages/shared/tests/ag-ui/LICENSE.
 *
 * Строгость: у событий AG-UI `unevaluatedProperties: false`, поэтому здесь `strictObject`. Незнакомое поле или тип —
 * отказ: коннектор и сервер одной версии, и незнакомое у нас — ошибка (thread-events.md §4). Типы, которые 006 не пишет
 * (`STATE_*`, `MESSAGES_SNAPSHOT`, `STEP_*`, `*_CHUNK`, `RAW`, `SUBAGENT_*`, `REASONING_ENCRYPTED_VALUE`), в схему не входят.
 */
import { z } from 'zod';

const SAFE_INT = z.number().int().min(-9007199254740991).max(9007199254740991);
const COUNT = z.number().int().min(0).max(9007199254740991);
/** `not: { type: null }` — любое значение JSON, кроме null. */
const NotNull = z.custom<unknown>((v) => v !== null && v !== undefined, { message: 'не null' });

export const Metadata = z.record(z.string(), z.unknown());

const base = {
  timestamp: SAFE_INT.optional(),
  rawEvent: NotNull.optional(),
  metadata: Metadata.optional(),
};
const attributable = { subagentRunId: z.string().optional() };

export const TokenUsage = z.strictObject({
  provider: z.string().optional(),
  model: z.string().optional(),
  inputTokens: COUNT.optional(),
  outputTokens: COUNT.optional(),
  totalTokens: COUNT.optional(),
  reasoningTokens: COUNT.optional(),
  cachedInputTokens: COUNT.optional(),
  cacheWriteInputTokens: COUNT.optional(),
});
export type TokenUsage = z.infer<typeof TokenUsage>;

export const Interrupt = z.strictObject({
  ...attributable,
  id: z.string(),
  reason: z.string(),
  message: z.string().optional(),
  toolCallId: z.string().optional(),
  responseSchema: z.record(z.string(), z.unknown()).optional(),
  expiresAt: z.string().optional(),
  metadata: Metadata.optional(),
});
export type Interrupt = z.infer<typeof Interrupt>;

export const ResumeEntry = z.strictObject({
  interruptId: z.string(),
  status: z.enum(['resolved', 'cancelled']),
  payload: NotNull.optional(),
  metadata: Metadata.optional(),
});
export type ResumeEntry = z.infer<typeof ResumeEntry>;

const TextPart = z.strictObject({
  type: z.literal('text'),
  id: z.string().optional(),
  text: z.string(),
  metadata: NotNull.optional(),
});
/** Медиа-части 006 не пишет; в результатах инструментов их нет. Принимаются только для совместимости разбора. */
const MediaPart = z.strictObject({
  type: z.enum(['image', 'audio', 'video', 'document']),
  id: z.string().optional(),
  source: z.record(z.string(), z.unknown()),
  metadata: NotNull.optional(),
});
export const ContentPart = z.union([TextPart, MediaPart]);
const Content = z.union([z.string(), z.array(ContentPart)]);

/** Сообщение пользователя в `RUN_STARTED.input.messages` — единственное, которое пишет 006. */
export const UserMessage = z.strictObject({
  ...attributable,
  id: z.string(),
  role: z.literal('user'),
  content: Content,
  name: z.string().optional(),
  encryptedValue: z.string().optional(),
  metadata: Metadata.optional(),
});
export type UserMessage = z.infer<typeof UserMessage>;

export const RunAgentInput = z.strictObject({
  threadId: z.string(),
  runId: z.string(),
  protocolVersion: z.string().optional(),
  parentRunId: z.string().optional(),
  messages: z.array(UserMessage),
  resume: z.array(ResumeEntry).optional(),
});

export const RunFinishedOutcome = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('success'), pendingToolCallIds: z.array(z.string()).optional() }),
  z.strictObject({ type: z.literal('interrupt'), interrupts: z.array(Interrupt).min(1) }),
  z.strictObject({ type: z.literal('cancelled') }),
]);
export type RunFinishedOutcome = z.infer<typeof RunFinishedOutcome>;

export const RunStarted = z.strictObject({
  ...base,
  type: z.literal('RUN_STARTED'),
  threadId: z.string(),
  runId: z.string(),
  protocolVersion: z.string().optional(),
  parentRunId: z.string().optional(),
  input: RunAgentInput.optional(),
});
export const RunFinished = z.strictObject({
  ...base,
  type: z.literal('RUN_FINISHED'),
  threadId: z.string(),
  runId: z.string(),
  result: NotNull.optional(),
  outcome: RunFinishedOutcome.optional(),
  usage: z.array(TokenUsage).optional(),
});
export const RunError = z.strictObject({
  ...base,
  type: z.literal('RUN_ERROR'),
  message: z.string(),
  code: z.string().optional(),
  usage: z.array(TokenUsage).optional(),
});

const TextMessageRole = z.enum(['developer', 'system', 'assistant', 'user']);
export const TextMessageStart = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TEXT_MESSAGE_START'),
  messageId: z.string(),
  role: TextMessageRole.optional(),
  name: z.string().optional(),
});
export const TextMessageContent = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TEXT_MESSAGE_CONTENT'),
  messageId: z.string(),
  delta: z.string(),
});
export const TextMessageEnd = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TEXT_MESSAGE_END'),
  messageId: z.string(),
});

export const ReasoningStart = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('REASONING_START'),
  messageId: z.string(),
});
export const ReasoningMessageStart = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('REASONING_MESSAGE_START'),
  messageId: z.string(),
  role: z.literal('reasoning'),
});
export const ReasoningMessageContent = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('REASONING_MESSAGE_CONTENT'),
  messageId: z.string(),
  delta: z.string(),
});
export const ReasoningMessageEnd = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('REASONING_MESSAGE_END'),
  messageId: z.string(),
});
export const ReasoningEnd = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('REASONING_END'),
  messageId: z.string(),
});

export const ToolCallStart = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TOOL_CALL_START'),
  toolCallId: z.string(),
  toolCallName: z.string(),
  parentMessageId: z.string().optional(),
});
export const ToolCallArgs = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TOOL_CALL_ARGS'),
  toolCallId: z.string(),
  delta: z.string(),
});
export const ToolCallEnd = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TOOL_CALL_END'),
  toolCallId: z.string(),
});
export const ToolCallResult = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('TOOL_CALL_RESULT'),
  messageId: z.string(),
  toolCallId: z.string(),
  content: Content,
  role: z.literal('tool').optional(),
});

const JsonPatchOp = z.strictObject({
  op: z.enum(['add', 'remove', 'replace', 'move', 'copy', 'test']),
  path: z.string(),
  value: z.unknown().optional(),
  from: z.string().optional(),
});
export const ActivitySnapshot = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('ACTIVITY_SNAPSHOT'),
  messageId: z.string(),
  activityType: z.string(),
  content: z.record(z.string(), z.unknown()),
  replace: z.boolean().optional(),
});
export const ActivityDelta = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('ACTIVITY_DELTA'),
  messageId: z.string(),
  activityType: z.string(),
  patch: z.array(JsonPatchOp),
});

export const Custom = z.strictObject({
  ...base,
  ...attributable,
  type: z.literal('CUSTOM'),
  name: z.string(),
  value: z.unknown().refine((v) => v !== undefined, 'value обязателен'),
});

/** Все события AG-UI, которые принимает журнал 006. */
export const AgUiEvent = z.discriminatedUnion('type', [
  RunStarted,
  RunFinished,
  RunError,
  TextMessageStart,
  TextMessageContent,
  TextMessageEnd,
  ReasoningStart,
  ReasoningMessageStart,
  ReasoningMessageContent,
  ReasoningMessageEnd,
  ReasoningEnd,
  ToolCallStart,
  ToolCallArgs,
  ToolCallEnd,
  ToolCallResult,
  ActivitySnapshot,
  ActivityDelta,
  Custom,
]);
export type AgUiEvent = z.infer<typeof AgUiEvent>;
export type AgUiEventType = AgUiEvent['type'];

/** Схема события по типу — для проверки образцов AG-UI (tests/ag-ui/fixtures). */
export const AG_UI_EVENT_SCHEMAS = {
  RunStartedEvent: RunStarted,
  RunFinishedEvent: RunFinished,
  RunErrorEvent: RunError,
  TextMessageStartEvent: TextMessageStart,
  TextMessageContentEvent: TextMessageContent,
  TextMessageEndEvent: TextMessageEnd,
  ReasoningStartEvent: ReasoningStart,
  ReasoningMessageStartEvent: ReasoningMessageStart,
  ReasoningMessageContentEvent: ReasoningMessageContent,
  ReasoningMessageEndEvent: ReasoningMessageEnd,
  ReasoningEndEvent: ReasoningEnd,
  ToolCallStartEvent: ToolCallStart,
  ToolCallArgsEvent: ToolCallArgs,
  ToolCallEndEvent: ToolCallEnd,
  ToolCallResultEvent: ToolCallResult,
  ActivitySnapshotEvent: ActivitySnapshot,
  ActivityDeltaEvent: ActivityDelta,
  CustomEvent: Custom,
  Interrupt,
  ResumeEntry,
} as const;

export const AG_UI_PROTOCOL_VERSION = '1.0';
